import { useEffect, useRef, useState, type ReactNode } from 'react'
import { writeRun, type Written } from '../../../api/auditFindings'
import { toast } from '../../../ui/notify'

/**
 * Raportul sau solutiile unei rulari, cu scrisul de mana peste ele -
 * `data-pane="report|solutions"` din `snapshot.php`, si `write()` din
 * `audit.js`.
 *
 * Caseta poarta textul BRUT, nu ce s-a randat: cine editeaza corecteaza ce a
 * scris agentul, nu HTML-ul rezultat din el. Ce a scris agentul se pastreaza
 * oricum (`Runs::edit` copiaza ciorna in `<ce>_ai` la prima editare), si
 * documentul spune ca a fost editat, de cine si cand (`hand`).
 *
 * Butonul "Edit before it is signed" sta in capul sectiunii, la cine cheama:
 * panoul primeste `open` si `onClose`, si atat. Cand `open` e fals, nu se
 * vede decat textul scris.
 *
 * Tastatura, cum se asteapta oricine intr-o caseta de scris: Escape inchide,
 * Cmd/Ctrl+Enter salveaza. Si garda de iesire: o caseta deschisa cu text
 * schimbat opreste inchiderea paginii - un raport de audit scris pe jumatate
 * si pierdut la un click pe meniu e exact felul de pierdere pe care n-o
 * reclama nimeni si o sufera toata lumea.
 */
export function WrittenPane({
  run,
  what,
  html,
  text,
  hand,
  draftKept,
  open,
  onClose,
  onWritten,
  empty,
  note,
}: {
  run: number
  what: Written
  /** Textul randat de server (`Scheduler::html`); gol = nescris inca. */
  html: string
  /** Textul brut, cel din caseta. */
  text: string
  /** "Drafted by the agent, then edited by ... on ..." - gol cand nu s-a editat. */
  hand: string
  /** Exista `<ce>_ai`? Atunci se poate intoarce la ciorna agentului. */
  draftKept: boolean
  open: boolean
  onClose: () => void
  /** Dupa o scriere reusita, cu ce a raspuns serverul. */
  onWritten?: (answer: { html: string; text: string; hand: string }) => void
  /** Ce se arata cand nu e nimic scris - propozitia depinde de starea rularii. */
  empty?: ReactNode
  /** Nota de sub caseta: de citit o data, nu la fiecare salvare. */
  note?: ReactNode
}) {
  const [shown, setShown] = useState({ html, text, hand, draftKept })
  const [draft, setDraft] = useState(text)
  const [busy, setBusy] = useState<'' | 'save' | 'restore'>('')
  const area = useRef<HTMLTextAreaElement>(null)

  /* Serverul a rescris textul (o alta fereastra, o generare noua): caseta
     porneste de la el, nu de la ce era. */
  useEffect(() => {
    setShown({ html, text, hand, draftKept })
    setDraft(text)
  }, [html, text, hand, draftKept])

  useEffect(() => {
    if (open) area.current?.focus()
  }, [open])

  const dirty = open && draft !== shown.text

  useEffect(() => {
    if (!dirty) return
    const guard = (ev: BeforeUnloadEvent) => {
      ev.preventDefault()
      ev.returnValue = 'The report has been changed and not saved.'
    }
    window.addEventListener('beforeunload', guard)
    return () => window.removeEventListener('beforeunload', guard)
  }, [dirty])

  function cancel() {
    /* Intreaba INAINTE sa arunce. Cineva care a scris zece minute intr-o
       caseta si apasa Cancel din reflex nu vrea sa piarda tot. */
    if (dirty && !window.confirm('Throw away what you wrote and go back to the saved text?')) return
    setDraft(shown.text)
    onClose()
  }

  async function write(restore: boolean) {
    setBusy(restore ? 'restore' : 'save')
    try {
      const r = await writeRun(run, what, draft, restore)
      if (!r.ok) {
        toast(r.error || 'It could not be saved.', 'IT Audit', 'error')
        return
      }
      /* Si textul de referinta, nu doar ce se vede: altfel garda de iesire
         crede in continuare ca e nesalvat, si Cancel ar intreba despre o
         ciorna care nu mai exista. */
      setShown({ html: r.html, text: r.text, hand: r.hand, draftKept: true })
      setDraft(r.text)
      onWritten?.({ html: r.html, text: r.text, hand: r.hand })
      onClose()
      toast(restore ? "Back to the agent's draft." : 'Saved. The document says it was edited.', 'IT Audit')
    } catch {
      toast('It could not be saved.', 'IT Audit', 'error')
    } finally {
      setBusy('')
    }
  }

  return (
    <div className={`adm-card od-audit-report${what === 'solutions' ? ' od-audit-solutions' : ''}`} data-pane={what}>
      {shown.hand.trim() !== '' ? (
        <p className="od-audit-hand">
          <i className="fas fa-pen" aria-hidden="true"></i> {shown.hand}
        </p>
      ) : null}

      {open ? null : shown.html !== '' ? (
        <div className="od-audit-written" dangerouslySetInnerHTML={{ __html: shown.html }} />
      ) : (
        <div className="od-audit-written">{empty ?? <p className="adm-muted">Not written yet.</p>}</div>
      )}

      {open ? (
        <div className="od-audit-editor">
          <textarea
            ref={area}
            className="form-control"
            rows={16}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Escape') {
                cancel()
                return
              }
              if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) {
                ev.preventDefault()
                void write(false)
              }
            }}
          />
          {/* Butoanele imediat sub caseta, unde le cauta mana. */}
          <div className="od-audit-editor-do">
            <button
              type="button"
              className="btn btn-primary btn-sm od-audit-save"
              disabled={busy !== ''}
              onClick={() => void write(false)}
            >
              {busy === 'save' ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="btn btn-default btn-sm od-audit-cancel" disabled={busy !== ''} onClick={cancel}>
              Cancel
            </button>
            <span className="od-audit-keys">&#8984;&#9166; to save, Esc to close</span>
            {shown.draftKept ? (
              <button
                type="button"
                className="btn btn-link btn-sm od-audit-restore"
                disabled={busy !== ''}
                onClick={() => void write(true)}
              >
                {busy === 'restore' ? 'Restoring...' : "Restore the agent's draft"}
              </button>
            ) : null}
          </div>
          <p className="adm-muted">
            {note ??
              `What the agent wrote is kept whatever you do here, and the document says the ${what} ${
                what === 'solutions' ? 'were' : 'was'
              } edited, by whom and when.`}
          </p>
        </div>
      ) : null}
    </div>
  )
}
